// Page Synthese 1/2 - Scores et Radars - V3 Multi-metiers

import { useEffect, useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { trackEvent } from '../lib/analytics'
import { Button, Card, CardContent, CardHeader, CardTitle } from '../components/ui'
import { DiagnosticLayout, useDiagnosticSteps, useDiagnosticData, useDiagnosticProgress } from '../components/layout'
import {
  RadarChart,
  RadarLegend,
  ScoreDonut,
  type ScoresAxes,
  type MaxParAxe,
} from '../components/results'
import RadarChartManagement, {
  RadarLegendManagement,
  MANAGEMENT_AXES,
  type ScoresManagement,
} from '../components/results/RadarChartManagement'
import { Separator } from '../components/ui/separator'
import { Progress } from '../components/ui/progress'
import { api } from '../services/api'

interface ReponseData {
  questionId: string
  metierId?: string
  valeur: number
}

// Axes HALTES
const HALTES_AXES = ['H', 'A', 'LT', 'E', 'S'] as const

export default function ResultsPage() {
  const { id: diagnosticId } = useParams<{ id: string }>()
  const navigate = useNavigate()

  // Charger les données du diagnostic (depuis state ou IndexedDB)
  const { metierIds, metiersSelectionnes, diagnosticState, isLoadingData } = useDiagnosticData(diagnosticId)

  const [reponses, setReponses] = useState<ReponseData[]>([])
  const [questionsQ1, setQuestionsQ1] = useState<any[]>([])
  const [questionsQ2, setQuestionsQ2] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Compter les metiers completes (ceux qui ont des reponses Q1)
  const completedMetiersCount = useMemo(() => {
    return metierIds.filter(metierId =>
      reponses.some(r => r.metierId === metierId)
    ).length
  }, [metierIds, reponses])

  // Charger la progression du diagnostic pour le stepper
  const diagnosticProgress = useDiagnosticProgress(diagnosticId)

  // Generer les etapes de progression avec info multi-metiers
  const steps = useDiagnosticSteps('resultats', diagnosticId, {
    totalMetiers: metierIds.length,
    currentMetierIndex: Math.max(0, completedMetiersCount - 1),
    q2Completed: reponses.some(r => !r.metierId), // Q2 = reponses sans metierId
    metiersSelectionnes: metiersSelectionnes.map(m => ({ code: m.code, nom: m.nom })),
  }, diagnosticProgress, 'resultats-scores')

  // Charger les donnees
  useEffect(() => {
    async function loadData() {
      if (!diagnosticId) return

      setIsLoading(true)
      setError(null)

      try {
        const [diagnostic, q1, q2] = await Promise.all([
          api.getDiagnostic(diagnosticId),
          api.getQuestionsQ1(),
          api.getQuestionsQ2(),
        ])

        // Filet de sécurité : si le diagnostic a des réponses mais pas de resultat,
        // appeler terminerDiagnostic pour calculer les scores côté serveur
        // (peut arriver si l'utilisateur a navigué via le stepper au lieu du bouton Q2)
        if (!diagnostic.resultat && (diagnostic.reponses || []).length > 0) {
          try {
            await api.terminerDiagnostic(diagnosticId)
          } catch {
            // Pas bloquant — les scores sont aussi calculés côté client
          }
        }

        const reponsesData = (diagnostic.reponses || []).map((r: any) => ({
          ...r,
          metierId: r.metier?.code || r.metierId,
        }))
        setReponses(reponsesData)
        setQuestionsQ1(q1)
        setQuestionsQ2(q2)

        if (reponsesData.length === 0) {
          setError('Aucune réponse trouvée pour ce diagnostic')
        }
      } catch (err: any) {
        console.error('Erreur chargement resultats:', err)
        const detail = err?.message || err?.status || String(err)
        setError(`Impossible de charger les résultats (${detail})`)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [diagnosticId])

  // Verifier quels metiers sont incomplets (Q1)
  const metiersStatus = useMemo(() => {
    return metierIds.map((metierId) => {
      const metierReponses = reponses.filter(r => r.metierId === metierId)
      const isComplete = metierReponses.length > 0
      const metier = metiersSelectionnes.find(m => m.code === metierId)
      return {
        metierId,
        nom: metier?.nom || metierId,
        isComplete,
        reponsesCount: metierReponses.length,
      }
    })
  }, [metierIds, reponses, metiersSelectionnes])

  const metiersIncomplete = metiersStatus.filter(m => !m.isComplete)
  const hasIncompleteMetiers = metiersIncomplete.length > 0

  // Calculer les scores Q1 par metier et par axe
  const scoresQ1ByMetier = useMemo(() => {
    const result = new Map<string, { scores: ScoresAxes; maxScores: MaxParAxe; total: number; max: number }>()

    metierIds.forEach((metierId) => {
      const scores: ScoresAxes = { H: 0, A: 0, LT: 0, E: 0, S: 0 }
      const counts: ScoresAxes = { H: 0, A: 0, LT: 0, E: 0, S: 0 }

      const metierReponses = reponses.filter(r => r.metierId === metierId)

      metierReponses.forEach((reponse) => {
        const question = questionsQ1.find(q => q.id === reponse.questionId)
        if (question) {
          const axe = (question.axe || question.axeHaltes) as keyof ScoresAxes
          if (axe && HALTES_AXES.includes(axe as typeof HALTES_AXES[number])) {
            scores[axe] += reponse.valeur
            counts[axe] += 1
          }
        }
      })

      const maxScores: MaxParAxe = {
        H: counts.H * 3,
        A: counts.A * 3,
        LT: counts.LT * 3,
        E: counts.E * 3,
        S: counts.S * 3,
      }

      const total = Object.values(scores).reduce((a, b) => a + b, 0)
      const max = Object.values(maxScores).reduce((a, b) => a + b, 0)

      result.set(metierId, { scores, maxScores, total, max })
    })

    return result
  }, [metierIds, reponses, questionsQ1])

  // Calculer les scores Q2 (management)
  const scoresQ2Data = useMemo(() => {
    const scores: ScoresManagement = {
      orientations: 0,
      roles: 0,
      gestion: 0,
      animation: 0,
      bilan: 0,
      audits: 0,
    }
    const counts: ScoresManagement = {
      orientations: 0,
      roles: 0,
      gestion: 0,
      animation: 0,
      bilan: 0,
      audits: 0,
    }

    const q2Reponses = reponses.filter(r => !r.metierId)

    q2Reponses.forEach((reponse) => {
      const question = questionsQ2.find(q => q.id === reponse.questionId)
      if (question && question.sousMethode) {
        const axeConfig = MANAGEMENT_AXES.find(a => a.code === question.sousMethode)
        if (axeConfig) {
          const axeKey = axeConfig.key
          scores[axeKey] += reponse.valeur
          counts[axeKey] += 1
        }
      }
    })

    const maxScores: ScoresManagement = {
      orientations: counts.orientations * 3,
      roles: counts.roles * 3,
      gestion: counts.gestion * 3,
      animation: counts.animation * 3,
      bilan: counts.bilan * 3,
      audits: counts.audits * 3,
    }

    return { scores, maxScores }
  }, [reponses, questionsQ2])

  // Score Q2 global
  const scoreQ2Global = useMemo(() => {
    const totalScore = Object.values(scoresQ2Data.scores).reduce((a, b) => a + b, 0)
    const totalMax = Object.values(scoresQ2Data.maxScores).reduce((a, b) => a + b, 0)
    return { score: totalScore, max: totalMax }
  }, [scoresQ2Data])

  // Scores globaux cumules
  const globalScores = useMemo(() => {
    let scoreQ1 = 0
    let maxQ1 = 0

    scoresQ1ByMetier.forEach((data) => {
      scoreQ1 += data.total
      maxQ1 += data.max
    })

    return {
      scoreTotal: scoreQ1 + scoreQ2Global.score,
      maxTotal: maxQ1 + scoreQ2Global.max,
      scoreQ1,
      maxQ1,
    }
  }, [scoresQ1ByMetier, scoreQ2Global])

  // Navigation
  const handleGoToSynthese2 = () => {
    trackEvent('voir l\'analyse detaillee')
    navigate(`/diagnostic/${diagnosticId}/synthese-2`, {
      state: diagnosticState,
    })
  }

  const handleGoBack = () => {
    navigate(`/diagnostic/${diagnosticId}/q2`, {
      state: diagnosticState,
    })
  }

  const handleGoToQ1 = (metierId: string) => {
    navigate(`/diagnostic/${diagnosticId}/q1`, {
      state: { ...diagnosticState, focusMetierId: metierId },
    })
  }

  if (isLoading || isLoadingData) {
    return (
      <DiagnosticLayout
        steps={steps}
        showNavigation={false}

        diagnosticState={diagnosticState}
      >
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-muted-foreground">Calcul des résultats...</p>
          </div>
        </div>
      </DiagnosticLayout>
    )
  }

  if (error) {
    return (
      <DiagnosticLayout
        steps={steps}
        showNavigation={false}

        diagnosticState={diagnosticState}
      >
        <div className="flex items-center justify-center py-16">
          <Card className="max-w-md w-full">
            <CardContent>
              <div className="text-center">
                <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-foreground mb-2">Erreur</h2>
                <p className="text-muted-foreground mb-4">{error}</p>
                <Button onClick={() => navigate('/')}>
                  Retour a l'accueil
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DiagnosticLayout>
    )
  }

  return (
    <DiagnosticLayout
      steps={steps}
      onBack={handleGoBack}
      onNext={hasIncompleteMetiers ? undefined : handleGoToSynthese2}
      backLabel="Retour"
      nextLabel="Voir l'analyse détaillée"
      nextDisabled={hasIncompleteMetiers}

      diagnosticState={diagnosticState}
    >
      <div className="space-y-6">
        {/* Header de page */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">Résultats - Scores et radars</h1>
          <p className="text-sm text-muted-foreground mt-1">Vue d'ensemble de votre évaluation par métier</p>
        </div>

        {/* Avertissement metiers incomplets */}
        {hasIncompleteMetiers && (
          <div className="bg-muted border border-border rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-foreground text-sm font-medium">
                  Completez le questionnaire Risques pour :
                  {metiersIncomplete.map((m, i) => (
                    <button
                      key={m.metierId}
                      onClick={() => handleGoToQ1(m.metierId)}
                      className="ml-1 underline hover:text-primary"
                    >
                      {m.nom}{i < metiersIncomplete.length - 1 ? ',' : ''}
                    </button>
                  ))}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* SCORE GLOBAL CARD */}
        <Card>
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
              {/* Donut */}
              <div className="flex-shrink-0">
                <ScoreDonut
                  score={globalScores.scoreTotal}
                  maxScore={globalScores.maxTotal}
                />
              </div>

              {/* Progress bars */}
              <div className="flex-1 w-full space-y-4">
                {/* Risques */}
                <div>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                    Risques
                  </h3>
                  <div className="space-y-3">
                    {metierIds.map((metierId, index) => {
                      const metier = metiersSelectionnes[index]
                      const data = scoresQ1ByMetier.get(metierId)
                      const score = data?.total || 0
                      const max = data?.max || 0
                      const percent = max > 0 ? Math.round((score / max) * 100) : 0
                      const status = metiersStatus.find(m => m.metierId === metierId)

                      return (
                        <div key={metierId}>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm font-medium text-foreground truncate">
                              {metier?.nom || metierId}
                              {!status?.isComplete && (
                                <span className="ml-1 text-xs text-muted-foreground">*</span>
                              )}
                            </span>
                            <span className="text-sm font-semibold text-foreground ml-2">
                              {score}/{max}
                            </span>
                          </div>
                          <Progress value={percent} className="h-2" />
                        </div>
                      )
                    })}
                  </div>
                </div>

                <Separator />

                {/* Management */}
                <div>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                    Management
                  </h3>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-foreground">Tous métiers</span>
                    <span className="text-sm font-semibold text-foreground">
                      {scoreQ2Global.score}/{scoreQ2Global.max}
                    </span>
                  </div>
                  <Progress
                    value={scoreQ2Global.max > 0 ? Math.round((scoreQ2Global.score / scoreQ2Global.max) * 100) : 0}
                    className="h-2"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* RADARS SECTION — always 2 columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Risques (HALTES) — radar superposé multi-métiers (story 11) */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">Risques (HALTES)</CardTitle>
            </CardHeader>
            <CardContent>
              {metierIds.length === 1 ? (
                <>
                  <RadarChart
                    scores={scoresQ1ByMetier.get(metierIds[0])?.scores || { H: 0, A: 0, LT: 0, E: 0, S: 0 }}
                    maxScores={scoresQ1ByMetier.get(metierIds[0])?.maxScores}
                  />
                  <RadarLegend
                    scores={scoresQ1ByMetier.get(metierIds[0])?.scores || { H: 0, A: 0, LT: 0, E: 0, S: 0 }}
                    maxScores={scoresQ1ByMetier.get(metierIds[0])?.maxScores}
                    compact
                  />
                </>
              ) : (
                <RadarChart
                  series={metierIds.map((metierId, index) => {
                    const data = scoresQ1ByMetier.get(metierId)
                    const metier = metiersSelectionnes[index]
                    return {
                      name: metier?.nom || metierId,
                      scores: data?.scores || { H: 0, A: 0, LT: 0, E: 0, S: 0 },
                      maxScores: data?.maxScores || { H: 0, A: 0, LT: 0, E: 0, S: 0 },
                    }
                  })}
                />
              )}
            </CardContent>
          </Card>

          {/* Management — always visible */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">Management</CardTitle>
            </CardHeader>
            <CardContent>
              <RadarChartManagement scores={scoresQ2Data.scores} maxScores={scoresQ2Data.maxScores} />
              <RadarLegendManagement scores={scoresQ2Data.scores} maxScores={scoresQ2Data.maxScores} compact />
            </CardContent>
          </Card>
        </div>
      </div>

    </DiagnosticLayout>
  )
}
